import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/* ------------------------------------------------------------------ */
/*  ThemeProvider — manages light / dark / system preference.           */
/*                                                                      */
/*  Persists the user's choice in localStorage and applies the matching */
/*  `.dark` class to <html>. When mode === 'system' it tracks the OS    */
/*  preference live via prefers-color-scheme.                           */
/*                                                                      */
/*  Pair with the inline script in index.html (sets `.dark` BEFORE      */
/*  React hydrates) to prevent flash of incorrect theme (FOUC).         */
/* ------------------------------------------------------------------ */

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'hjalptorget:theme'

interface ThemeContextValue {
  /** What the user picked (may be 'system'). */
  theme: ThemeMode
  /** What's actually applied right now ('light' or 'dark'). */
  resolvedTheme: ResolvedTheme
  /** Set the theme mode (persists to localStorage). */
  setTheme: (mode: ThemeMode) => void
  /** Cycle through light → dark → system → light. */
  cycleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStored(): ThemeMode {
  if (typeof window === 'undefined') return 'system'
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    // localStorage unavailable
  }
  return 'system'
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  // Sync the <meta name="theme-color"> so iOS Safari / Android Chrome
  // tint the browser chrome to match the active theme.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = resolved === 'dark' ? '#0f1219' : '#2d6040'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStored())
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => readSystemTheme())

  // Track OS preference changes so 'system' mode reacts live.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light')
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme

  // Apply class on every change.
  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode)
    try {
      window.localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // ignore
    }
  }, [])

  const cycleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')
  }, [theme, setTheme])

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    // Fallback so the hook doesn't crash in tests / Storybook without the
    // provider mounted. Reads OS preference and persists to localStorage.
    return {
      theme: readStored(),
      resolvedTheme: readStored() === 'system' ? readSystemTheme() : (readStored() as ResolvedTheme),
      setTheme: (m) => {
        try { window.localStorage.setItem(STORAGE_KEY, m) } catch { /* */ }
        const resolved: ResolvedTheme = m === 'system' ? readSystemTheme() : m
        applyTheme(resolved)
      },
      cycleTheme: () => undefined,
    }
  }
  return ctx
}

/**
 * Returns the inline script string that should be injected into the
 * document <head> *before* React hydrates, so the correct theme is
 * applied during the initial paint and there's no flash of light mode.
 *
 * Used in index.html (copy this string verbatim into a <script> tag).
 */
export const THEME_INIT_SCRIPT = `(function(){try{var s=localStorage.getItem('${STORAGE_KEY}');var t=(s==='light'||s==='dark')?s:(window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light');if(t==='dark')document.documentElement.classList.add('dark');var m=document.querySelector('meta[name=theme-color]');if(m)m.content=t==='dark'?'#0f1219':'#2d6040';}catch(e){}})();`
